/**
 * <variant-picker> -- progressive enhancement for the option pills inside the product form.
 * No framework: reads the embedded variant JSON, matches the selected option combination to a
 * real variant, and updates the hidden variant id input, price, availability text, the two
 * primary action buttons, the variant-specific description block (see snippets/
 * product-description.liquid), the composed H1 title, and the gallery image. Without
 * JavaScript the form still works (it submits the first available variant's id, which is
 * rendered server-side as the default selection); the anchor-hack description falls back to
 * showing just the intro text and the plain gallery, no per-variant swap.
 */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variant-json]').textContent);
    this.addEventListener('change', (e) => { this.syncOptions(e.target); this.onChange(); });
    this.syncOptions(null);

    const initialId = this.closest('form')?.querySelector('[data-variant-id-input]')?.value;
    const initialVariant = this.variants.find((v) => String(v.id) === String(initialId));
    if (initialVariant) this.applySideEffects(initialVariant);
  }

  onChange() {
    const selected = Array.from(this.querySelectorAll('input[type="radio"]:checked')).map((el) => el.value);
    const variant = this.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));

    // The hidden id input sits in the product form next to (not inside) this element, so look it
    // up via the form; this.querySelector alone never found it (Welle 2, 29.09.2026).
    const idInput = this.closest('form')?.querySelector('[data-variant-id-input]') || this.querySelector('[data-variant-id-input]');
    const priceEl = document.querySelector('[data-product-price]');
    const submitButtons = document.querySelectorAll('[data-requires-variant]');

    // Auftrag "Lagerbestand darf nie als Verfuegbarkeitsaussage erscheinen" (23.09.2026,
    // KRITISCH): dieser Schritt aktualisierte frueher zusaetzlich einen [data-product-availability]
    // Badge mit "In stock"/"Currently out of stock"-Text (Locale-Keys products.availability.*,
    // inzwischen aus allen Sprachdateien entfernt). Dieser Badge existiert nicht mehr (siehe
    // sections/product.liquid) -- die Verfuegbarkeit steuert seit jeher NUR das disabled-Attribut
    // der beiden Aktions-Buttons, das bleibt unveraendert.
    if (!variant) {
      submitButtons.forEach((btn) => (btn.disabled = true));
      return;
    }

    if (idInput) idInput.value = variant.id;
    if (priceEl && variant.price != null) {
      priceEl.textContent = this.formatMoney(variant.price);
    }
    submitButtons.forEach((btn) => {
      btn.disabled = !variant.available;
    });

    this.applySideEffects(variant);
    this.dispatchEvent(new CustomEvent('variant:changed', { bubbles: true, detail: { variant } }));
  }

  /**
   * A46 (Strategie 04.10.2026): dependent options. A value of option 2, 3 … is only offered when a variant exists with the
   * options chosen before it; other values are hidden and disabled. If the current choice became invalid (e.g. after a
   * series change), the first valid value is chosen. Purely from the variant options, no handles. Applies to every product
   * that uses this picker (old product page, v60, spare parts).
   */
  /**
   * B6 (08.10.2026): Optionen, deren sichtbare Werte ALLE mit einer Zahl beginnen (z. B. 55, 38, 65 cm), werden aufsteigend
   * angezeigt (CSS order am Label, numerisch nach der führenden Zahl; Gleichstand: Datenreihenfolge). Textoptionen und gemischte
   * Optionen bleiben in Datenreihenfolge. Nur Anzeige: Werte, IDs und Auswahllogik bleiben unverändert. Läuft nach jeder Synchronisierung,
   * weil abhängige Optionen Werte ein- und ausblenden.
   */
  sortNumericOptions() {
    const groups = {};
    this.querySelectorAll('input[type="radio"][name^="option-"]').forEach((el) => { (groups[el.name] = groups[el.name] || []).push(el); });
    Object.keys(groups).forEach((name) => {
      const items = groups[name].map((el, idx) => {
        const lab = el.id ? this.querySelector('label[for="' + el.id + '"]') : null;
        const wrap = lab || (el.parentElement && el.parentElement.tagName === 'LABEL' ? el.parentElement : null);
        return { el: el, wrap: wrap, idx: idx, shown: !el.disabled && !(wrap && wrap.style.display === 'none') };
      });
      items.forEach((i) => { if (i.wrap) i.wrap.style.order = ''; });
      const vis = items.filter((i) => i.shown && i.wrap);
      if (vis.length < 2 || !vis.every((i) => /^\s*\d/.test(i.el.value))) return;
      const num = (i) => parseFloat(i.el.value.replace(',', '.').match(/^\s*(\d+(?:[.,]\d+)?)/)[1].replace(',', '.'));
      vis.slice().sort((a, b) => (num(a) - num(b)) || (a.idx - b.idx)).forEach((i, k) => { i.wrap.style.order = String(k + 1); });
    });
  }

  syncOptions(changed) {
    const names = [];
    this.querySelectorAll('input[type="radio"][name^="option-"]').forEach((el) => { if (!names.includes(el.name)) names.push(el.name); });
    if (names.length < 2) { this.sortNumericOptions(); return; }
    const chosen = [];
    names.forEach((name, i) => {
      const radios = Array.from(this.querySelectorAll('input[type="radio"][name="' + name + '"]'));
      if (i > 0) {
        const valid = (val) => this.variants.some((v) => v.options[i] === val && chosen.every((c, j) => v.options[j] === c));
        let firstValid = null;
        radios.forEach((el) => {
          const ok = valid(el.value);
          el.disabled = !ok;
          const lab = el.id ? this.querySelector('label[for="' + el.id + '"]') : null;
          const wrap = lab || (el.parentElement && el.parentElement.tagName === 'LABEL' ? el.parentElement : null);
          if (wrap) wrap.style.display = ok ? '' : 'none';
          if (ok && !firstValid) firstValid = el;
        });
        const cur = radios.find((el) => el.checked);
        if ((!cur || cur.disabled) && firstValid) firstValid.checked = true;
      }
      const now = radios.find((el) => el.checked);
      chosen.push(now ? now.value : null);
    });
    this.sortNumericOptions();
  }

  applySideEffects(variant) {
    this.updateComposedTitle(variant);
    this.updateDescriptionBlock(variant);
    this.updateGalleryImage(variant);
  }

  /**
   * Replaces StarApps Variant Title King. Verified live on the Vintage theme (07.08.):
   * "{vendor} - {variant.sku} - {product.title}", each part only included when present.
   *
   * Welle 3.5 (11.08.2026): when the SKU is identical to the title, or already contained in it
   * (case-insensitive), drop the SKU part instead of showing it twice -- mirrors the same rule
   * applied server-side in sections/product.liquid so the title doesn't "un-fix" itself on the
   * very first variant change.
   *
   * Designkritik Runde 6, Befund 1 (15.08.2026): dropped the vendor segment. The eyebrow line
   * above the H1 already shows the vendor, so repeating it inside the H1 read as a copy-paste
   * duplicate -- same fix, same reasoning as sections/product.liquid's composed_title.
   */
  updateComposedTitle(variant) {
    // Nachbesserung Welle 2 (30.09.2026, P2): vessel pages show the static short name in the H1
    // (marked data-short-name, no data-composed-title). The variant change must never put the old
    // shop title back, so only an H1 that explicitly asks for the composed title is rewritten.
    const h1 = document.querySelector('[data-composed-title]');
    if (!h1 || h1.hasAttribute('data-short-name')) return;
    // T-0034 (AUFTRAG-114): Ersatzteile – die H1 je Variante kommt fertig aus sections/product-v60-part.liquid
    // ([data-variant-h1], Gruppen A/B/C, übersetzte Optionswerte). Fehlt die Liste, gilt die Regel unten.
    if (this.h1Map === undefined) {
      const src = document.querySelector('[data-variant-h1]');
      try { this.h1Map = src ? JSON.parse(src.textContent) : null; } catch (e) { this.h1Map = null; }
    }
    if (this.h1Map && this.h1Map[String(variant.id)]) {
      h1.textContent = this.h1Map[String(variant.id)];
      return;
    }
    const title = h1.dataset.productTitle || '';
    const sku = variant.sku || '';
    const skuInTitle = sku && title.toLowerCase().includes(sku.toLowerCase());
    // Projektleiter-Festlegung 29.09.2026 (F9): the SKU is also dropped when it begins with the
    // start of the title (the model name before the first " - "), e.g. SKU "AC 2XL35-L - MDR" on
    // "AC 2XL35-L - Long Canisters" -- otherwise the H1 read "AC 2XL35-L - MDR - AC 2XL35-L - ...".
    // Mirrors the server-side rule in sections/product.liquid.
    const titleStart = (title.split(' - ')[0] || '').trim().toLowerCase();
    const skuStartsLikeTitle = sku && titleStart && sku.toLowerCase().startsWith(titleStart);
    const parts = [skuInTitle || skuStartsLikeTitle ? null : sku, title].filter(Boolean);
    h1.textContent = parts.join(' - ');
  }

  updateDescriptionBlock(variant) {
    const script = document.querySelector('[data-description-blocks]');
    const container = document.querySelector('[data-description-variant-block]');
    if (!script || !container) return;

    if (!this.descriptionBlocks) {
      try {
        this.descriptionBlocks = JSON.parse(script.textContent);
      } catch (e) {
        this.descriptionBlocks = [];
      }
    }

    const key = this.pickDescriptionBlock(variant, this.descriptionBlocks);
    const block = this.descriptionBlocks.find((b) => b.key === key);
    container.innerHTML = block ? block.html : '';
  }

  /**
   * Reverse-engineered from the live catalog (see snippets/product-description.liquid comment):
   * try the most specific anchor (all option values normalised, joined by "-"), then drop
   * trailing options one at a time, finally fall back to a literal "#All"/"#ALL" block.
   */
  pickDescriptionBlock(variant, blocks) {
    if (!blocks || !blocks.length) return null;
    const keys = new Set(blocks.map((b) => b.key));
    /* H-162 (06.10.2026): the anchors keep "+" and brackets ("#XL+-30-LONG", "#XL+-11-SHORT(10)",
       "#3XL-175(bloodbag)"). Stripping them made "XL+ / 30 / LONG" hit the "#XL-30-LONG" block of the
       XL series. So first compare with "+" and brackets kept, then with the old fully stripped rule. */
    const keepSpecial = (v) => (v || '').replace(/[^A-Za-z0-9+()]/g, '');
    const stripAll = (v) => (v || '').replace(/[^A-Za-z0-9]/g, '');
    /* AUFTRAG-88 Nr. 2: Die Anker sind aus den Quell-Optionswerten (Englisch) gebildet, die angezeigten Werte sind übersetzt.
       Darum zuerst die Quellwerte dieser Varianten-ID (snippets/variant-canon, JSON "data-description-keys"); nur ohne
       Eintrag (neue Variante) die angezeigten Werte. */
    if (this.canonKeys === undefined) {
      this.canonKeys = null;
      const keyScript = document.querySelector('[data-description-keys]');
      if (keyScript) {
        try { this.canonKeys = JSON.parse(keyScript.textContent); } catch (e) { this.canonKeys = null; }
      }
    }
    const canon = this.canonKeys && this.canonKeys[String(variant.id)];
    const raw = (canon && canon.length ? canon : [variant.option1, variant.option2, variant.option3]).filter(Boolean);
    const exact = raw.map(keepSpecial);
    const parts = raw.map(stripAll);

    /* JA-LIVE-2026-10-25-1910 (tolerante Marken): Ein Block kann mehrere Schlüssel tragen („#750-8   #750-24“), und ein
       Schlüssel kann Sonderzeichen enthalten („#"NitrogenService"“). Jeder Teilschlüssel zählt roh, mit „+“/Klammern und ganz
       bereinigt; Ergebnis ist der Schlüssel des Blocks (so wie im JSON). Exakte Treffer gehen weiter vor. „#None“ passt nie. */
    const alias = new Map();
    blocks.forEach((b) => {
      String(b.key || '').split('#').map((s) => s.trim()).filter((s) => s && s.toLowerCase() !== 'none').forEach((sub) => {
        ['#' + sub, '#' + keepSpecial(sub), '#' + stripAll(sub)].forEach((k) => { if (k !== '#' && !alias.has(k)) alias.set(k, b.key); });
      });
    });
    /* AUFTRAG-115 C1 (28.10.2026): Optionswerte mit Sonderzeichen („1 / 2“, „0,25 ml / 0,5 ml“) wurden zu „#12-BRASS“, die Marke
       heißt „#1-2-BRASS“ (Marken-Seite wird über keepSpecial/stripAll ohnehin ganz zusammengezogen). Darum je Stufe nach den
       bisherigen Schlüsseln zusätzlich ohne Bindestrich zwischen den Werten vergleichen, zuletzt ohne Groß-/Kleinschreibung
       („3 / NONE“ → „#3-None“). Bisherige Treffer bleiben unverändert, weil sie je Stufe zuerst geprüft werden. */
    const aliasUp = new Map();
    alias.forEach((v, k) => { const u = k.toUpperCase(); if (!aliasUp.has(u)) aliasUp.set(u, v); });
    keys.forEach((k) => { const u = String(k).toUpperCase(); if (!aliasUp.has(u)) aliasUp.set(u, k); });
    const find = (k) => (keys.has(k) ? k : alias.get(k));
    for (let n = parts.length; n >= 1; n--) {
      const tries = ['#' + exact.slice(0, n).join('-'), '#' + parts.slice(0, n).join('-'), '#' + exact.slice(0, n).join(''), '#' + parts.slice(0, n).join('')];
      for (const k of tries) { const hit = find(k); if (hit) return hit; }
      for (const k of tries) { const hit = aliasUp.get(k.toUpperCase()); if (hit) return hit; }
    }
    const fallback = [...keys].find((k) => k.trim().toLowerCase() === '#all');
    return fallback || null;
  }

  updateGalleryImage(variant) {
    // variant.featured_image.id is the legacy Image API id -- a different id space from
    // product.media (and thus from the "Media-<id>" elements product-gallery.liquid renders).
    // variant.featured_media.id is the one that actually matches (verified against a live
    // multi-variant product: featured_image.id was ~10^13 in one id space, featured_media.id
    // matched a real "Media-<id>" element exactly).
    // 02.10.2026: the product gallery (assets/product-gallery.js) moves itself, keeps its counter,
    // thumbnails and zoom position in sync and never scrolls the page.
    // V3 (06.10.2026, doku/regeln/VARIANTEN-BILDER-LOGIK.md): it also filters to the variant's images,
    // so the variant id is sent too -- also for variants without a main image (= all images).
    if (document.querySelector('[data-pg]')) {
      document.dispatchEvent(new CustomEvent('gallery:select', { detail: { mediaId: variant.featured_media ? variant.featured_media.id : null, variantId: variant.id } }));
      return;
    }
    if (!variant.featured_media) return;
    const media = document.getElementById('Media-' + variant.featured_media.id);
    if (media) {
      media.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
    document.querySelectorAll('.product-gallery__thumb').forEach((thumb) => {
      thumb.classList.toggle('is-active', thumb.getAttribute('href') === '#Media-' + variant.featured_media.id);
    });
  }

  formatMoney(cents) {
    // Preisformat je Sprache (Variante b): same formatter as snippets/price.liquid (layout/theme.liquid).
    if (typeof window.cryonosMoney === 'function') return window.cryonosMoney(cents);
    // Falls back to a plain amount if Shopify.formatMoney / theme money format isn't available.
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.themeMoneyFormat) {
      return window.Shopify.formatMoney(cents, window.themeMoneyFormat);
    }
    // Welle 2: Shopify.formatMoney is not loaded on the storefront, so apply the shop's money
    // format (window.themeMoneyFormat, layout/theme.liquid) directly; the price showed "32.00"
    // without currency after a variant change before.
    const format = window.themeMoneyFormat ? String(window.themeMoneyFormat).replace(/<[^>]*>/g, '') : '';
    if (format) {
      const group = (n, t, d) => {
        const [i, f] = (n / 100).toFixed(2).split('.');
        return i.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (d === null ? '' : d + f);
      };
      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
        switch (key) {
          case 'amount_no_decimals': return group(cents, ',', null);
          case 'amount_with_comma_separator': return group(cents, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return group(cents, '.', null);
          case 'amount_with_apostrophe_separator': return group(cents, "'", '.');
          default: return group(cents, ',', '.');
        }
      });
    }
    return (cents / 100).toFixed(2);
  }
}

customElements.define('variant-picker', VariantPicker);
